import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import { $postSpecialOfferStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import type { GetSpecialOffersResponse } from '@shared/api';
import { ConfirmModal } from '@widgets';
import { Button, Spinner, Text } from '@shared/widgets';
import ConfirmIcon from '@assets/icons/confirm.svg';

interface OfferProps {
  className?: string;
  type: string;
  offer: GetSpecialOffersResponse;
}

export const Offer = (props: OfferProps) => {
  const { navigate } = useRouteHook();

  const postSpecialOfferStore = useUnit($postSpecialOfferStore.store);

  const { i18n, t } = useTranslation('concierge');

  const offerHandler = async () => await $postSpecialOfferStore.events.postSpecialOffer({ specialOfferId: props.offer.id });
  const confirmHandler = () => {
    navigate(RoutePaths.concierge.onNavigate());
    $postSpecialOfferStore.events.clear();
  };

  return (
    <section
      className={cn(
        'flex w-full justify-center bg-white p-6 dark:bg-soft-black',
        PaletteUtils.isVcfcPalette && 'rounded-xl dark:border dark:border-gray-black',
        props.className,
      )}
    >
      <Spinner show={postSpecialOfferStore.loading} position />
      <div className={cn('grid w-full max-w-3xl gap-8 overflow-hidden', 'max-lg:gap-4 lg:grid-cols-2')}>
        <img className={cn('h-64 w-full object-cover', 'lg:h-full lg:min-h-[360px]')} src={props.offer.imageUrl} alt={props.offer.header} />
        <div className={cn('flex flex-col gap-4')}>
          <div className='flex flex-wrap items-center justify-between gap-3'>
            <Text size='sm' weight='semibold' color='secondary'>
              {t('offer.validUntil', { date: dayjs(props.offer.to).locale(i18n.language).format('D MMMM YYYY') })}
            </Text>
            <Text
              className={cn(
                'bg-ghost-white px-3 py-1 capitalize dark:bg-eerie-black',
                PaletteUtils.isInsigniaPalette && 'rounded-sm',
                PaletteUtils.isVcfcPalette && 'rounded-xl',
              )}
              size='sm'
            >
              {props.type}
            </Text>
          </div>
          <Text tag='h2' weight='semibold' size='2xl'>
            {props.offer.header}
          </Text>
          <Text tag='p' color='secondary'>
            {props.offer.description}
          </Text>
          {props.offer.subtitle && (
            <Text tag='h3' weight='semibold' size='lg'>
              {props.offer.subtitle}
            </Text>
          )}
          <Text tag='p' className='whitespace-pre-line' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(props.offer.content) }} />
          <Button className='mt-auto' fullWidth color='primary' disabled={postSpecialOfferStore.loading} onClick={offerHandler}>
            {t('buttons.request', { ns: 'common' })}
          </Button>
        </div>
      </div>
      <ConfirmModal
        isOpen={!!postSpecialOfferStore.data?.id}
        icon={<ConfirmIcon />}
        title={t('confirm.title')}
        description={t('confirm.description')}
        buttonTitle={t('confirm.button')}
        onClick={confirmHandler}
      />
    </section>
  );
};
